import React from 'react';
import { reportClientError, type ClientErrorType } from '@/services/errorReportingService';

interface State {
  error: Error | null;
}

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
];

const HOOKS_ERROR_PATTERNS = [
  /Rendered fewer hooks than expected/i,
  /Rendered more hooks than during the previous render/i,
  /Should have a queue\. This is likely a bug in React/i,
];

const RELOAD_FLAG = 'munilex_hooks_autoreload';

function isChunkError(err: unknown): boolean {
  const msg = String((err as Error)?.message || err || '');
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg));
}

function isHooksError(err: unknown): boolean {
  const msg = String((err as Error)?.message || err || '');
  return HOOKS_ERROR_PATTERNS.some((p) => p.test(msg));
}

/**
 * ErrorBoundary específico para fallos de carga de chunks dinámicos
 * (típico tras un deploy o reinicio del dev-server) y para errores de
 * inconsistencia de hooks tras login (race condition de hidratación de sesión).
 * Muestra UI clara con botón de reintento en vez de pantalla en blanco.
 */
export class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Logger detallado: incluimos el componentStack para identificar
    // exactamente qué componente está rompiendo. Buscar en consola
    // "[LazyErrorBoundary]" cuando vuelva a aparecer la pantalla de error.
    console.error('[LazyErrorBoundary] Error capturado:', error);
    console.error('[LazyErrorBoundary] Component stack:', errorInfo.componentStack);

    const errorType: ClientErrorType = isHooksError(error)
      ? 'hooks_mismatch'
      : isChunkError(error)
        ? 'chunk_load'
        : 'runtime';

    // Auto-recarga UNA SOLA VEZ ante errores de hooks (típico race condition
    // entre el primer render sin sesión y el segundo con profile cargado).
    // Usamos sessionStorage para evitar bucles de recarga infinitos.
    let willAutoReload = false;
    if (isHooksError(error)) {
      try {
        const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG);
        if (!alreadyReloaded) {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
          willAutoReload = true;
          console.warn('[LazyErrorBoundary] Hooks mismatch detectado tras login → auto-recarga única');
        } else {
          console.error('[LazyErrorBoundary] Hooks mismatch persistente tras auto-recarga: requiere fix manual');
        }
      } catch {
        // sessionStorage no disponible: dejamos que el usuario reintente manualmente
      }
    }

    // Reportar a la BBDD para que admin/super_admin lo vea en /admin/errors.
    // Best-effort: no bloquea la auto-recarga.
    void reportClientError({
      type: errorType,
      error,
      componentStack: errorInfo.componentStack,
      autoReloaded: willAutoReload,
    });

    if (willAutoReload) {
      // Pequeño delay para que el log se vea en consola y dé tiempo al insert
      setTimeout(() => window.location.reload(), 250);
    }
  }


  handleRetry = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const chunk = isChunkError(this.state.error);
      return (
        <div
          className="flex min-h-screen w-full items-center justify-center p-6"
          style={{ background: 'linear-gradient(135deg,#f0fdfa 0%,#ccfbf1 100%)' }}
        >
          <div className="max-w-md w-full text-center bg-white rounded-2xl border border-border shadow-xl p-7">
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-2">
              Munilex
            </div>
            <h1 className="text-xl font-bold text-foreground mb-3">
              {chunk ? 'Actualizando aplicación' : 'Se ha producido un error'}
            </h1>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              {chunk
                ? 'Hemos detectado una versión nueva. Recarga para continuar trabajando con la última versión.'
                : 'Ha ocurrido un problema inesperado. Vuelve a intentarlo o recarga la página.'}
            </p>
            <button
              onClick={this.handleRetry}
              className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
